import { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { PositionChangeInput } from './PositionChangeInput'
import type { AdjustmentScenario, PositionChange, Asset, Portfolio } from '@/types/api'

// Form validation schema
const adjustmentFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(50, 'Name must be at most 50 characters'),
  description: z.string().max(200, 'Description must be at most 200 characters').optional(),
})

type AdjustmentFormValues = z.infer<typeof adjustmentFormSchema>

interface AdjustmentFormProps {
  adjustment?: AdjustmentScenario | null
  portfolio: Portfolio
  assets: Asset[]
  existingAdjustments: AdjustmentScenario[]
  onSubmit: (data: { name: string; description?: string; changes: PositionChange[] }) => void
  onCancel: () => void
  isSubmitting?: boolean
}

/**
 * AdjustmentForm - Comprehensive form for creating/editing adjustment scenarios
 * 
 * Features:
 * - React Hook Form with Zod validation
 * - Name and description fields with validation
 * - Position changes for all portfolio positions via PositionChangeInput
 * - Search/filter to find specific assets
 * - Running totals: total delta qty, delta value
 * - Visual diff showing before/after values
 * - Warning if >50% of portfolio being modified
 * - Edit mode support (pre-fills existing data)
 */
export function AdjustmentForm({
  adjustment,
  portfolio,
  assets,
  existingAdjustments,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: AdjustmentFormProps) {
  const isEditMode = !!adjustment
  const [searchQuery, setSearchQuery] = useState('')
  const [positionChanges, setPositionChanges] = useState<Record<string, { quantityDelta: number; newQuantity: number }>>(
    () => {
      // Initialize from existing adjustment if in edit mode
      if (adjustment) {
        const initial: Record<string, { quantityDelta: number; newQuantity: number }> = {}
        adjustment.changes.forEach((change) => {
          const currentQty = portfolio.positions.find((p) => p.assetId === change.assetId)?.quantity
          const currentQtyNum = currentQty ? parseFloat(currentQty as unknown as string) : 0
          const newQty = parseFloat(change.newQuantity)
          initial[change.assetId] = {
            quantityDelta: newQty - currentQtyNum,
            newQuantity: newQty,
          }
        })
        return initial
      }
      return {}
    }
  )

  // Form setup
  const form = useForm<AdjustmentFormValues>({
    resolver: zodResolver(adjustmentFormSchema),
    defaultValues: {
      name: adjustment?.name || '',
      description: adjustment?.description || '',
    },
  })

  // Create asset lookup map
  const assetMap = useMemo(() => {
    const map: Record<string, Asset> = {}
    assets.forEach((asset) => {
      map[asset.id] = asset
    })
    return map
  }, [assets])

  // Create position lookup with asset data
  const positionsWithAssets = useMemo(() => {
    return portfolio.positions
      .map((position) => {
        const asset = assetMap[position.assetId]
        if (!asset) return null
        return {
          position,
          asset,
          currentQuantity: parseFloat(position.quantity as unknown as string),
          currentValue: parseFloat(position.value as unknown as string),
        }
      })
      .filter(Boolean) as Array<{
        position: typeof portfolio.positions[0]
        asset: Asset
        currentQuantity: number
        currentValue: number
      }>
  }, [portfolio.positions, assetMap])

  // Filter positions by search query
  const filteredPositions = useMemo(() => {
    if (!searchQuery.trim()) return positionsWithAssets
    const query = searchQuery.toLowerCase()
    return positionsWithAssets.filter(
      (item) =>
        item.asset.symbol.toLowerCase().includes(query) ||
        item.asset.name.toLowerCase().includes(query)
    )
  }, [positionsWithAssets, searchQuery])

  // Group positions by asset class
  const groupedPositions = useMemo(() => {
    const groups: Record<string, typeof filteredPositions> = {
      CRYPTO: [],
      TRADFI: [],
    }
    filteredPositions.forEach((item) => {
      const classKey = item.asset.assetClass
      if (!groups[classKey]) groups[classKey] = []
      groups[classKey].push(item)
    })
    return groups
  }, [filteredPositions])

  // Calculate totals
  const totals = useMemo(() => {
    let totalPositionsChanged = 0
    let totalDeltaValue = 0
    let portfolioPercentModified = 0

    Object.entries(positionChanges).forEach(([assetId, change]) => {
      if (change.quantityDelta !== 0) {
        totalPositionsChanged++
        const position = positionsWithAssets.find((p) => p.asset.id === assetId)
        if (position) {
          const avgPrice = position.currentValue / position.currentQuantity
          totalDeltaValue += change.quantityDelta * avgPrice
        }
      }
    })

    const totalValue = parseFloat(portfolio.totalValue as unknown as string)
    if (totalValue > 0) {
      portfolioPercentModified = (Math.abs(totalDeltaValue) / totalValue) * 100
    }

    return {
      totalPositionsChanged,
      totalDeltaValue,
      portfolioPercentModified,
    }
  }, [positionChanges, positionsWithAssets, portfolio.totalValue])

  // Handle position change update
  const handlePositionChange = (assetId: string, delta: { quantityDelta: number; newQuantity: number }) => {
    setPositionChanges((prev) => ({
      ...prev,
      [assetId]: delta,
    }))
  }

  // Get initial values for a position (for edit mode)
  const getInitialValuesForPosition = (assetId: string) => {
    if (!adjustment) return undefined
    const change = adjustment.changes.find((c) => c.assetId === assetId)
    if (!change) return undefined

    const newQty = parseFloat(change.newQuantity)
    const position = positionsWithAssets.find((p) => p.asset.id === assetId)
    if (!position) return undefined

    const currentQty = position.currentQuantity
    const refPrice = parseFloat(position.asset.currentPrice as unknown as string)

    // Guard against division by zero
    if (currentQty === 0) return undefined

    const pct = ((newQty - currentQty) / currentQty) * 100
    const dollar = newQty * refPrice

    return {
      qty: newQty.toString(),
      pct: pct.toString(),
      dollar: dollar.toString(),
    }
  }

  // Form submission
  const handleSubmit = (values: AdjustmentFormValues) => {
    // Validate name uniqueness
    const isUnique = !existingAdjustments.some(
      (adj) =>
        adj.name.toLowerCase() === values.name.toLowerCase() &&
        adj.id !== adjustment?.id
    )
    if (!isUnique) {
      form.setError('name', {
        type: 'manual',
        message: 'An adjustment with this name already exists',
      })
      return
    }

    // Validate at least one position has a change
    const hasChanges = Object.values(positionChanges).some((change) => change.quantityDelta !== 0)
    if (!hasChanges) {
      // Show error - need at least one change
      alert('Please specify at least one position change')
      return
    }

    // Build changes array
    const changes: PositionChange[] = Object.entries(positionChanges)
      .filter(([, change]) => change.quantityDelta !== 0)
      .map(([assetId, change]) => ({
        assetId,
        symbol: positionsWithAssets.find((p) => p.asset.id === assetId)?.asset.symbol || '',
        newQuantity: change.newQuantity.toString(),
      }))

    onSubmit({
      name: values.name,
      description: values.description,
      changes,
    })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold">
            {isEditMode ? 'Edit Adjustment' : 'Create Adjustment'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {isEditMode
              ? 'Modify your portfolio adjustment scenario'
              : 'Define a new portfolio adjustment scenario for stress testing'}
          </p>
        </div>

        <Separator />

        {/* Section 1: Metadata */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700">Scenario Details</h3>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., Reduce Crypto Exposure"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Optional description of this adjustment scenario"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        {/* Section 2: Position Changes */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">Position Changes</h3>
            <Badge variant="secondary" className="text-xs">
              {totals.totalPositionsChanged} of {portfolio.positions.length} modified
            </Badge>
          </div>

          {/* Search */}
          <div>
            <Input
              placeholder="Search assets by symbol or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isSubmitting}
              className="max-w-md"
            />
          </div>

          {/* Warning for large modifications */}
          {totals.portfolioPercentModified > 50 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
              Warning: You&apos;re modifying more than 50% of your portfolio value. This is a
              significant adjustment.
            </div>
          )}

          {/* Grouped Positions */}
          <div className="space-y-6 max-h-96 overflow-y-auto pr-2">
            {['CRYPTO', 'TRADFI'].map((assetClass) => {
              const positions = groupedPositions[assetClass]
              if (positions.length === 0) return null

              return (
                <div key={assetClass} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant={assetClass === 'CRYPTO' ? 'default' : 'secondary'}>
                      {assetClass}
                    </Badge>
                    <span className="text-sm text-gray-500">({positions.length} positions)</span>
                  </div>

                  <div className="space-y-3">
                    {positions.map((item) => (
                      <PositionChangeInput
                        key={item.asset.id}
                        asset={{
                          id: item.asset.id,
                          symbol: item.asset.symbol,
                          name: item.asset.name,
                          currentPrice: parseFloat(item.asset.currentPrice as unknown as string),
                          assetClass: item.asset.assetClass,
                        }}
                        currentPosition={{
                          assetId: item.position.assetId,
                          quantity: item.currentQuantity,
                          value: item.currentValue,
                        }}
                        onChange={(delta) => handlePositionChange(item.asset.id, delta)}
                        disabled={isSubmitting}
                        initialValues={getInitialValuesForPosition(item.asset.id)}
                      />
                    ))}
                  </div>
                </div>
              )
            })}

            {filteredPositions.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                No positions match your search
              </p>
            )}
          </div>
        </div>

        <Separator />

        {/* Section 3: Summary */}
        <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700">Adjustment Summary</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Positions Changed</p>
              <p className="font-semibold">{totals.totalPositionsChanged}</p>
            </div>
            <div>
              <p className="text-gray-500">Total Value Impact</p>
              <p
                className={`font-semibold ${
                  totals.totalDeltaValue >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {totals.totalDeltaValue >= 0 ? '+' : ''}
                ${Math.abs(totals.totalDeltaValue).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Portfolio Impact</p>
              <p className="font-semibold">{totals.portfolioPercentModified.toFixed(2)}%</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 italic mt-2">
            Note: Adjustments are &quot;unfunded&quot; - the portfolio NAV may differ from baseline after
            applying this scenario.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || totals.totalPositionsChanged === 0}>
            {isSubmitting
              ? 'Saving...'
              : isEditMode
              ? 'Save Changes'
              : 'Create Adjustment'}
          </Button>
        </div>
      </form>
    </Form>
  )
}

export default AdjustmentForm
